import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  MenuItem,
  Slider,
  Snackbar,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CompareOutlinedIcon from '@mui/icons-material/CompareOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import GraphicEqOutlinedIcon from '@mui/icons-material/GraphicEqOutlined';
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined';
import { AngleSyncResult, syncAnglesBySound } from '../../utils/media/syncBySound';
import { MulticamHowItWorksNote } from './MulticamHowItWorks';
import ChoiceCardGroup, { ChoiceOption } from '../NewProject/ChoiceCardGroup';
import { applyMulticamCuts } from '../../services/speakersApi';
import { SequencedAngle, playsInSequence, sequenceCuts, sequenceStarts } from './multicamSequence';
import { toMutationInput } from './multicamStudioModel';
import SpeakerNameField from './SpeakerNameField';
import SeparateAudioSection, { SeparateAudioDraft, separateAudioProblem } from './SeparateAudioSection';
import { recordingDisplayName } from './separateAudio';
import LineUpByPictureDialog from './LineUpByPictureDialog';
import { PictureLineUpPlan, describeStart, formatMoment } from './lineUpByPicture';
import {
  fetchVideos,
  fetchMulticamSet,
  fetchMulticamAnnotations,
  saveMulticamSet
} from '../../graphqlService';
import { brand } from '../../theme';
import { formatBytes } from '../../utils/format';
import { isMulticamConflictError } from '../../utils/multicamConflict';
import PageContainer from '../ui/PageContainer';
import PageHeader from '../ui/PageHeader';
import Section from '../ui/Section';
import EmptyState from '../ui/EmptyState';
import LoadingState from '../ui/LoadingState';
import KeyValueList from '../ui/KeyValueList';
import LibraryPoster from '../Library/LibraryPoster';
import VideoChoice from '../Library/VideoChoice';
import { formatRunningTime, formatVideoDuration, joinMeta, objectIdTime, sortLibraryItems } from '../Library/libraryFormat';
import { friendlyErrorMessage } from '../Library/friendlyError';

interface Video {
  id: string;
  name: string;
  url: string;
  size: number;
  duration: string;
}

/** Up to six angles: the master (A) and five more. */
type AngleKey = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
const ANGLE_KEYS: AngleKey[] = ['A', 'B', 'C', 'D', 'E', 'F'];
const MAX_ANGLES = ANGLE_KEYS.length;

interface AngleConfig {
  angleKey: AngleKey;
  videoId: string;
  video: Video | null;
  label: string;
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs: number | null;
  /** Who's on this angle; undefined until typed (the server keeps what it has). */
  speakerName?: string;
}

// Parse a "H:MM:SS" / "M:SS" duration string to milliseconds.
const parseDurationToMs = (duration: string): number => {
  const parts = duration.split(':').map(p => parseInt(p, 10) || 0);
  if (parts.length === 3) {
    return (parts[0] * 3600 + parts[1] * 60 + parts[2]) * 1000;
  } else if (parts.length === 2) {
    return (parts[0] * 60 + parts[1]) * 1000;
  }
  return parts[0] * 1000;
};

// Duration strings are whole seconds, so a file can run up to a second longer.
const DURATION_ROUNDING_TOLERANCE_MS = 1000;

/**
 * How the set plays: multicam (every angle at once, lined up by sound or by
 * picture, and cut between) or joined clips (one after another, end to end).
 */
type Filmed = 'together' | 'separately';

const FILMED_OPTIONS: ChoiceOption<Filmed>[] = [
  {
    value: 'together',
    title: 'Multicam',
    description: 'Angles of the same moment play at once, and you cut between them. They line up by sound, or by picture.',
  },
  {
    value: 'separately',
    title: 'Join clips',
    description: 'Not multicam: the clips play one after another, each with its own sound.',
  },
];

/** The angles with a video, as the sequence helpers take them. */
const sequenceInput = (list: AngleConfig[]): SequencedAngle[] =>
  list
    .filter((angle) => angle.video)
    .map((angle) => ({
      angleKey: angle.angleKey,
      timelineStartMs: angle.angleKey === 'A' ? 0 : angle.timelineStartMs,
      sourceInMs: angle.sourceInMs,
      sourceOutMs: angle.sourceOutMs,
      durationMs: parseDurationToMs(angle.video!.duration),
    }));

/** The angles laid end to end: each starts when the one before it ends. */
const lineUpInSequence = (list: AngleConfig[]): AngleConfig[] => {
  const starts = sequenceStarts(sequenceInput(list));
  return list.map((angle) =>
    angle.angleKey !== 'A' && angle.video && starts[angle.angleKey] !== undefined
      ? { ...angle, timelineStartMs: starts[angle.angleKey] }
      : angle
  );
};

const sameCut = (a: { timelineTimeMs: number; targetAngleKey: string }, b: { timelineTimeMs: number; targetAngleKey: string }) =>
  a.targetAngleKey === b.targetAngleKey && Math.abs(a.timelineTimeMs - b.timelineTimeMs) <= 1;

/** "0:42.50": offsets and trims to 0.01s, the precision the fields take. */
const formatSeconds = (ms: number): string => formatMoment(ms);

/**
 * Mirrors the server's check so problems show next to the fields, before the
 * save. The server re-validates everything.
 */
const angleTimingProblem = (angle: AngleConfig): string | null => {
  const durationMs = angle.video ? parseDurationToMs(angle.video.duration) : 0;
  const limit = durationMs + DURATION_ROUNDING_TOLERANCE_MS;
  if (!Number.isFinite(angle.timelineStartMs) || angle.timelineStartMs < 0) {
    return 'The offset must be zero or more.';
  }
  if (!Number.isFinite(angle.sourceInMs) || angle.sourceInMs < 0) {
    return 'Trim start must be zero or more.';
  }
  if (durationMs > 0 && angle.sourceInMs >= limit) {
    return `Trim start is past the end of this video (${formatSeconds(durationMs)}).`;
  }
  if (angle.sourceOutMs !== null) {
    if (angle.sourceOutMs <= angle.sourceInMs) return 'Trim end must be after trim start.';
    if (durationMs > 0 && angle.sourceOutMs > limit) {
      return `Trim end is past the end of this video (${formatSeconds(durationMs)}).`;
    }
  }
  return null;
};

/** "Master (angle A)", "Close-up (angle B)", or just "Angle B" for an unnamed angle. */
const angleDisplayName = (angle: AngleConfig): string => {
  const label = angle.label.trim();
  return label && label !== `Angle ${angle.angleKey}` ? `${label} (angle ${angle.angleKey})` : `Angle ${angle.angleKey}`;
};

/** Where an angle sits on the timeline, for the review. */
const timingSummary = (angle: AngleConfig): string => {
  const parts = [`Starts at ${formatSeconds(angle.angleKey === 'A' ? 0 : angle.timelineStartMs)}`];
  if (angle.sourceInMs > 0 || angle.sourceOutMs !== null) {
    parts.push(`Trim ${formatSeconds(angle.sourceInMs)} to ${angle.sourceOutMs !== null ? formatSeconds(angle.sourceOutMs) : 'end'}`);
  }
  return parts.join(' · ');
};

/** The set's running time, or null while any angle's length is unknown. */
const setLengthMs = (configured: AngleConfig[]): number | null => {
  let end = 0;
  for (const angle of configured) {
    const durationMs = angle.video ? parseDurationToMs(angle.video.duration) : 0;
    const sourceEnd = angle.sourceOutMs ?? durationMs;
    if (!(sourceEnd > 0)) return null;
    const start = angle.angleKey === 'A' ? 0 : angle.timelineStartMs;
    end = Math.max(end, start + Math.max(0, sourceEnd - angle.sourceInMs));
  }
  return end > 0 ? end : null;
};

const videoMetaText = (video: Video) =>
  joinMeta([formatVideoDuration(video.duration), video.size > 0 ? formatBytes(video.size) : null]);

/** "12.48 s" */
const formatSyncSeconds = (ms: number): string => `${(Math.abs(ms) / 1000).toFixed(2)} s`;

/** One angle's sync-by-sound outcome; `adjusted` once its timing is edited by hand afterwards. */
type SoundSyncOutcome = AngleSyncResult & { adjusted?: boolean };

/** An angle lined up by picture: the moments picked put its start `offsetMs` after the master's. */
interface PictureLineUp {
  key: string;
  status: 'picture';
  offsetMs: number;
  adjusted?: boolean;
}

/** How an angle was last lined up, by sound or by picture. */
type LineUpOutcome = SoundSyncOutcome | PictureLineUp;

const isLinedUp = (outcome?: LineUpOutcome): boolean => outcome?.status === 'matched' || outcome?.status === 'picture';

interface SoundSyncRun {
  status: 'idle' | 'running' | 'done' | 'cancelled';
  fraction: number;
  /** The angle being listened to. */
  currentKey: string | null;
}

/** How an angle was lined up, in words. */
const describeLineUp = (outcome: LineUpOutcome): { severity: 'success' | 'warning' | 'info'; text: string } => {
  if (outcome.status === 'picture') {
    const when = describeStart(outcome.offsetMs);
    return {
      severity: outcome.adjusted ? 'info' : 'success',
      text: outcome.adjusted ? `Lined up by picture, then adjusted by hand. ${when}` : `Lined up by picture. ${when}`,
    };
  }
  if (outcome.status === 'matched') {
    const match = outcome.estimate.quality === 'strong' ? 'Strong match' : 'Good match, worth a quick check';
    const offsetMs = Math.round(outcome.estimate.offsetS * 1000);
    const when = offsetMs >= 0
      ? `This camera started ${formatSyncSeconds(offsetMs)} after the master.`
      : `This camera started ${formatSyncSeconds(offsetMs)} before the master, so that much is trimmed from its start.`;
    return {
      severity: outcome.adjusted ? 'info' : 'success',
      text: outcome.adjusted
        ? `Lined up by sound, then adjusted by hand. ${when}`
        : `Lined up by sound. ${when} ${match}.`,
    };
  }
  if (outcome.status === 'weak') {
    return {
      severity: 'info',
      text: 'The sound didn’t match clearly enough to be sure, so nothing was changed. Line it up by picture, or set its start below.',
    };
  }
  // The reasons are shared with the separate recording, which has no picture to line up by.
  return { severity: 'warning', text: outcome.reason.replace(/Set this (one|angle) by hand\.$/, 'Line it up by picture, or set its start below.') };
};

const STEPS = [
  {
    label: 'Name',
    title: 'Name the set',
    description: "A name you'll recognize in your library and projects."
  },
  {
    label: 'Master video',
    title: 'Choose the master video',
    description: 'The master sets the timeline, so pick the angle that covers the whole recording.'
  },
  {
    label: 'More angles',
    title: 'Add more angles',
    description: 'Add up to five more camera angles, or skip this step to use just the master.'
  },
  {
    label: 'Sync',
    title: 'Line up the angles',
    description: 'Multicam angles play at the same time, lined up by their sound or by picture. Or join the clips to play them one after another.'
  },
  {
    label: 'Review',
    title: 'Review and save',
    description: 'Check the set before you save it.'
  }
];
const LAST_STEP = STEPS.length - 1;
const SYNC_STEP = 3;
// Section derives its heading id from its own id.
const STEP_SECTION_ID = 'multicam-step';
const STEP_HEADING_ID = `${STEP_SECTION_ID}-title`;
const LIBRARY_MULTICAM_TAB = '/library?tab=multicam';

// The default MUI stepper, with upcoming step numbers that stay readable
// (slate on warm grey instead of white on light grey).
const stepperSx = {
  mb: 3,
  '& .MuiStepLabel-label': { color: 'text.secondary', fontWeight: 500 },
  '& .MuiStepLabel-label.Mui-active': { color: 'text.primary', fontWeight: 700 },
  '& .MuiStepLabel-label.Mui-completed': { color: 'text.primary' },
  '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': { mt: 1 },
  '& .MuiStepIcon-root': { color: 'grey.300' },
  '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': { color: 'primary.main' },
  '& .MuiStepIcon-root:not(.Mui-active) .MuiStepIcon-text': { fill: brand.slate },
  '& .MuiStepButton-root.Mui-focusVisible': { outline: `2px solid ${brand.copper}`, outlineOffset: -2, borderRadius: 2 },
};

interface LoadProblem {
  message: string;
  canRetry: boolean;
}

const MulticamNew: React.FC = () => {
  const navigate = useNavigate();
  const { multicamSetId } = useParams<{ multicamSetId?: string }>();

  const isEditMode = Boolean(multicamSetId);

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const stepRegionRef = useRef<HTMLDivElement>(null);
  const movedStep = useRef(false);

  // Form state
  const [name, setName] = useState('');
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  // A failed load replaces the form: there is nothing to edit without it.
  const [loadProblem, setLoadProblem] = useState<LoadProblem | null>(null);
  const [saving, setSaving] = useState(false);
  // Set once the save succeeds and stays set until the redirect. `saving`
  // clears in `finally`, which re-enabled "Create multicam set" for the
  // 1.5s before navigation - a second click created a second set.
  const [saved, setSaved] = useState(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // The set's updatedAt as loaded (edit mode). Sent with the save so the
  // server refuses it if the set was saved elsewhere in the meantime -
  // saves used to be last-write-wins across tabs and devices.
  const [loadedUpdatedAt, setLoadedUpdatedAt] = useState<string | null>(null);
  // The last save was refused because the set changed elsewhere; the error
  // then offers a Reload.
  const [conflict, setConflict] = useState(false);
  const [reloading, setReloading] = useState(false);

  // Angle configurations
  const [angles, setAngles] = useState<AngleConfig[]>([
    { angleKey: 'A', videoId: '', video: null, label: 'Master', timelineStartMs: 0, sourceInMs: 0, sourceOutMs: null }
  ]);

  // Audio source angle
  const [audioAngleKey, setAudioAngleKey] = useState<string>('A');
  // A separate recording (recorder or mixer), the set's sound when present.
  const [separateAudio, setSeparateAudio] = useState<SeparateAudioDraft | null>(null);
  const [separateAudioBusy, setSeparateAudioBusy] = useState(false);
  const masterVideo = angles.find(a => a.angleKey === 'A')?.video;
  const masterDurationMs = masterVideo ? parseDurationToMs(masterVideo.duration) : 0;

  // Sync by sound
  const [soundSync, setSoundSync] = useState<SoundSyncRun>({ status: 'idle', fraction: 0, currentKey: null });
  const [lineUpResults, setLineUpResults] = useState<Record<string, LineUpOutcome>>({});
  // The angle open in "Line up by picture".
  const [pictureAngleKey, setPictureAngleKey] = useState<AngleKey | null>(null);
  const soundSyncAbortRef = useRef<AbortController | null>(null);
  // Offered once, automatically, the first time a new set reaches the sync step.
  const autoSyncOfferedRef = useRef(false);
  const [filmed, setFilmed] = useState<Filmed>('together');
  /** The cuts between clips a saved set of joined clips already had, to move or remove on save. */
  const loadedSequenceCutsRef = useRef<Array<{ timelineTimeMs: number; targetAngleKey: string }>>([]);

  // Don't navigate away from wherever the user went after leaving this page,
  // and stop listening to the angles' sound.
  useEffect(() => () => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    soundSyncAbortRef.current?.abort();
  }, []);

  // Results describe the videos they were made from; a different video makes them stale.
  const angleVideosKey = angles.map(a => `${a.angleKey}:${a.videoId}`).join('|');
  useEffect(() => {
    soundSyncAbortRef.current?.abort();
    soundSyncAbortRef.current = null;
    setLineUpResults({});
    setSoundSync({ status: 'idle', fraction: 0, currentKey: null });
  }, [angleVideosKey]);

  // Load videos (and the existing set if editing). Also used by "Reload"
  // after a conflicting save, which replaces the form with the latest
  // version of the set.
  const loadData = useCallback(async (): Promise<boolean> => {
    try {
      setLoading(true);
      setLoadProblem(null);
      const fetchedVideos = await fetchVideos();
      // Newest first, as in the Library: recent uploads are the likeliest angles.
      setVideos(sortLibraryItems(fetchedVideos as Video[], 'newest', {
        time: (video) => objectIdTime(video.id),
        name: (video) => video.name || ''
      }));

      // If editing, load the existing multicam set
      if (multicamSetId) {
        const existingSet = await fetchMulticamSet(multicamSetId);
        if (!existingSet) {
          setLoadProblem({ message: "We couldn't find this multicam set. It may have been deleted.", canRetry: false });
          return false;
        }
        if (existingSet.status === 'LOCKED') {
          setLoadProblem({
            message: "This multicam set was submitted with a project, so its set-up can't be changed.",
            canRetry: false
          });
          return false;
        }

        // Populate form from existing set
        setName(existingSet.name);
        setAudioAngleKey(existingSet.audioAngleKey || 'A');
        setLoadedUpdatedAt(existingSet.updatedAt || null);
        const recording = existingSet.audioSource;
        setSeparateAudio(recording ? {
          videoId: recording.videoId,
          label: recording.label,
          videoName: recording.videoName,
          videoUrl: recording.videoUrl,
          sizeBytes: recording.sizeBytes || 0,
          durationMs: recording.durationMs || 0,
          timelineStartMs: Math.max(0, recording.timelineStartMs || 0),
          sourceInMs: Math.max(0, recording.sourceInMs || 0),
          sourceOutMs: recording.sourceOutMs ?? null,
        } : null);

        const loadedAngles: AngleConfig[] = existingSet.angles.map(angle => {
          const video = fetchedVideos.find((v: Video) => v.id === angle.videoId) || null;
          return {
            angleKey: angle.angleKey as AngleKey,
            videoId: angle.videoId,
            video,
            label: angle.label,
            timelineStartMs: Math.max(0, angle.timelineStartMs), // Clamp legacy negative offsets
            sourceInMs: angle.sourceInMs,
            sourceOutMs: angle.sourceOutMs ?? null,
            ...(angle.speakerName ? { speakerName: angle.speakerName } : {})
          };
        });
        setAngles(loadedAngles);
        // A set saved with its clips end to end opens as joined clips.
        if (playsInSequence(sequenceInput(loadedAngles))) {
          setFilmed('separately');
          loadedSequenceCutsRef.current = sequenceCuts(sequenceInput(loadedAngles));
        }
      }
      return true;
    } catch (err: any) {
      console.error('Failed to load the multicam set-up:', err);
      setLoadProblem({
        message: multicamSetId
          ? "We couldn't load this multicam set. Check your connection and try again."
          : "We couldn't load your videos. Check your connection and try again.",
        canRetry: true
      });
      return false;
    } finally {
      setLoading(false);
      setInitialLoadDone(true);
    }
  }, [multicamSetId]);

  // On mount (and when the route switches to another set).
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleReloadAfterConflict = async () => {
    setReloading(true);
    setError(null);
    setConflict(false);
    try {
      if (await loadData()) {
        setSuccess('Loaded the latest version. Make your changes again, then save.');
      }
    } finally {
      setReloading(false);
    }
  };

  // Moving between steps takes focus to the new step, so keyboard and
  // screen-reader users land on it rather than on a button that went away.
  const goToStep = (step: number) => {
    movedStep.current = true;
    setActiveStep(step);
  };

  useEffect(() => {
    if (!movedStep.current) return;
    movedStep.current = false;
    stepRegionRef.current?.focus();
  }, [activeStep]);

  // Handle video selection for an angle
  const handleVideoSelect = (angleKey: AngleKey, videoId: string) => {
    const video = videos.find(v => v.id === videoId);
    setAngles(prev => prev.map(angle =>
      angle.angleKey === angleKey
        ? { ...angle, videoId, video: video || null }
        : angle
    ));
  };

  // The key "Add angle" will create: the first free one from B to F (add B
  // and C, remove B, and the next one is B again - the button used to say C).
  const nextAngleKey: AngleKey = ANGLE_KEYS.find((key) => key !== 'A' && !angles.some((a) => a.angleKey === key)) ?? 'B';

  // Add the next angle (B to F)
  const addAngle = () => {
    if (angles.length >= MAX_ANGLES) return;

    const nextKey = nextAngleKey;
    setAngles(prev => {
      if (prev.length >= MAX_ANGLES || prev.some(a => a.angleKey === nextKey)) return prev;
      // Keep A to F order so a re-added B lists before C.
      return [
        ...prev,
        {
          angleKey: nextKey,
          videoId: '',
          video: null,
          label: `Angle ${nextKey}`,
          timelineStartMs: 0,
          sourceInMs: 0,
          sourceOutMs: null
        } as AngleConfig
      ].sort((a, b) => a.angleKey.localeCompare(b.angleKey));
    });
  };

  // Remove an angle
  const removeAngle = (angleKey: AngleKey) => {
    if (angleKey === 'A') return; // Can't remove master
    setAngles(prev => prev.filter(a => a.angleKey !== angleKey));
    // Reset audio source to master if the removed angle was the audio source
    if (audioAngleKey === angleKey) {
      setAudioAngleKey('A');
    }
  };

  // Update angle label
  const updateAngleLabel = (angleKey: AngleKey, label: string) => {
    setAngles(prev => prev.map(angle =>
      angle.angleKey === angleKey ? { ...angle, label } : angle
    ));
  };

  const updateSpeakerName = (angleKey: AngleKey, speakerName: string) => {
    setAngles(prev => prev.map(angle =>
      angle.angleKey === angleKey ? { ...angle, speakerName } : angle
    ));
  };

  // A hand edit after sync by sound: say so instead of claiming the sound set it.
  const markAdjustedByHand = (angleKey: string) => {
    setLineUpResults(prev => (isLinedUp(prev[angleKey]) && !prev[angleKey].adjusted
      ? { ...prev, [angleKey]: { ...prev[angleKey], adjusted: true } }
      : prev));
  };

  /** Places an angle by the moments picked in "Line up by picture". */
  const applyPictureLineUp = (angleKey: AngleKey, plan: PictureLineUpPlan) => {
    setAngles(prev => prev.map(angle => (angle.angleKey === angleKey
      ? { ...angle, timelineStartMs: plan.timing.timelineStartMs, sourceInMs: plan.timing.sourceInMs }
      : angle)));
    setLineUpResults(prev => ({ ...prev, [angleKey]: { key: angleKey, status: 'picture', offsetMs: plan.offsetMs } }));
    setPictureAngleKey(null);
  };

  // Update angle sync offset
  const updateAngleOffset = (angleKey: AngleKey, timelineStartMs: number) => {
    setAngles(prev => prev.map(angle =>
      angle.angleKey === angleKey ? { ...angle, timelineStartMs } : angle
    ));
    markAdjustedByHand(angleKey);
  };

  // Update angle source in point
  const updateAngleSourceIn = (angleKey: AngleKey, sourceInMs: number) => {
    setAngles(prev => prev.map(angle =>
      angle.angleKey === angleKey ? { ...angle, sourceInMs } : angle
    ));
    markAdjustedByHand(angleKey);
  };

  /**
   * Reads the opening minutes of each angle's sound (the audio bytes only)
   * and sets each extra angle's start and trim to match the master. Results
   * arrive one angle at a time; a weak match changes nothing.
   */
  const runSoundSync = async () => {
    const master = angles.find(a => a.angleKey === 'A' && a.video);
    const others = angles.filter(a => a.angleKey !== 'A' && a.video);
    if (!master?.video || others.length === 0 || soundSync.status === 'running') return;
    soundSyncAbortRef.current?.abort();
    const controller = new AbortController();
    soundSyncAbortRef.current = controller;
    setLineUpResults({});
    setSoundSync({ status: 'running', fraction: 0, currentKey: master.angleKey });
    try {
      await syncAnglesBySound(
        { key: master.angleKey, url: master.video.url, sourceInMs: master.sourceInMs },
        others.map(angle => ({ key: angle.angleKey, url: angle.video!.url, sourceInMs: angle.sourceInMs })),
        {
          signal: controller.signal,
          onProgress: ({ fraction, key }) => {
            if (!controller.signal.aborted) setSoundSync({ status: 'running', fraction, currentKey: key });
          },
          onResult: (result) => {
            if (controller.signal.aborted) return;
            if (result.status === 'matched') {
              setAngles(prev => prev.map(angle => (angle.angleKey === result.key
                ? { ...angle, timelineStartMs: result.timing.timelineStartMs, sourceInMs: result.timing.sourceInMs }
                : angle)));
            }
            setLineUpResults(prev => ({ ...prev, [result.key]: result }));
          },
        }
      );
      if (!controller.signal.aborted) setSoundSync({ status: 'done', fraction: 1, currentKey: null });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') {
        if (soundSyncAbortRef.current === controller) setSoundSync({ status: 'cancelled', fraction: 0, currentKey: null });
        return;
      }
      console.error('Sync by sound failed:', err);
      setSoundSync({ status: 'done', fraction: 1, currentKey: null });
      setError('Sync by sound stopped unexpectedly. Set the offsets by hand, or try again.');
    } finally {
      if (soundSyncAbortRef.current === controller) soundSyncAbortRef.current = null;
    }
  };

  const cancelSoundSync = () => {
    soundSyncAbortRef.current?.abort();
  };

  /** Multicam lines the angles up by sound; joining lays the clips end to end. */
  const chooseFilmed = (value: Filmed) => {
    if (value === filmed) return;
    setFilmed(value);
    // Stop a sync in progress without it reporting "Stopped".
    const runningSync = soundSyncAbortRef.current;
    soundSyncAbortRef.current = null;
    runningSync?.abort();
    setLineUpResults({});
    setSoundSync({ status: 'idle', fraction: 0, currentKey: null });
    if (value === 'separately') {
      // Nothing to line up by sound: each clip plays with its own.
      autoSyncOfferedRef.current = true;
      setAudioAngleKey('A');
      setAngles((prev) => lineUpInSequence(prev));
    } else {
      // Back to lining up: every extra angle starts with the master again.
      setAngles((prev) => prev.map((angle) => (angle.angleKey === 'A' ? angle : { ...angle, timelineStartMs: 0 })));
    }
  };

  // Laid end to end, a changed trim or angle moves the clips after it.
  useEffect(() => {
    if (filmed !== 'separately') return;
    setAngles((prev) => {
      const next = lineUpInSequence(prev);
      return next.some((angle, index) => angle.timelineStartMs !== prev[index].timelineStartMs) ? next : prev;
    });
  }, [filmed, angles]);

  // The first visit to the sync step of a new set lines the angles up by
  // sound straight away, unless they were already set by hand.
  useEffect(() => {
    if (activeStep !== SYNC_STEP || isEditMode || autoSyncOfferedRef.current) return;
    const others = angles.filter(a => a.angleKey !== 'A' && a.video);
    if (others.length === 0 || others.some(a => a.timelineStartMs !== 0 || a.sourceInMs !== 0)) return;
    autoSyncOfferedRef.current = true;
    void runSoundSync();
    // Only the step change starts it; runSoundSync reads the latest angles itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // Update angle source out point
  const updateAngleSourceOut = (angleKey: AngleKey, sourceOutMs: number | null) => {
    setAngles(prev => prev.map(angle =>
      angle.angleKey === angleKey ? { ...angle, sourceOutMs } : angle
    ));
  };

  // Whether a step is filled in well enough to move past it.
  const isStepComplete = (step: number): boolean => {
    switch (step) {
      case 0: // Name
        return name.trim().length >= 3;
      case 1: // Master video
        return angles[0].videoId !== '';
      case 2: // Additional angles (optional)
        return true; // Can skip
      case 3: // Sync offsets (and the separate recording, once uploaded and valid)
        return angles.every(a => a.videoId !== '' || a.angleKey !== 'A') &&
          angles.filter(a => a.videoId).every(a => !angleTimingProblem(a)) &&
          !separateAudioBusy &&
          (!separateAudio || (separateAudio.videoId !== null && !separateAudioProblem(separateAudio)));
      case 4: // Review
        return true;
      default:
        return false;
    }
  };

  // A step can be opened from the stepper once every step before it is done.
  const canReachStep = (step: number): boolean => {
    for (let index = 0; index < step; index += 1) {
      if (!isStepComplete(index)) return false;
    }
    return true;
  };

  // Handle creating or updating the multicam set.
  //
  // One atomic server write. The old chain (create, add each angle, sync each
  // angle, set audio) had no rollback: a failure part-way left a half-built
  // set behind, and a retry could only guess what had already been saved.
  const handleSave = async () => {
    if (saving || saved) return;
    try {
      setSaving(true);
      setError(null);
      setConflict(false);

      const savedSet = await saveMulticamSet({
        multicamSetId: isEditMode ? multicamSetId : undefined,
        name: name.trim(),
        audioAngleKey,
        angles: angles.filter(a => a.videoId).map(a => ({
          angleKey: a.angleKey,
          videoId: a.videoId,
          label: a.label,
          timelineStartMs: a.angleKey === 'A' ? 0 : a.timelineStartMs,
          sourceInMs: a.sourceInMs,
          sourceOutMs: a.sourceOutMs,
          ...(a.speakerName !== undefined ? { speakerName: a.speakerName.trim() } : {}),
        })),
        audioSource: separateAudio?.videoId
          ? {
              videoId: separateAudio.videoId,
              label: separateAudio.label.trim(),
              timelineStartMs: separateAudio.timelineStartMs,
              sourceInMs: separateAudio.sourceInMs,
              sourceOutMs: separateAudio.sourceOutMs,
            }
          : null,
        // Edit mode only: a create has no earlier version to conflict with.
        ...(isEditMode && loadedUpdatedAt ? { expectedUpdatedAt: loadedUpdatedAt } : {}),
      });

      // Separate clips: a cut to each where it starts, so the studio, the
      // editor's player and the rough cut show them in order. A cut already
      // there is kept; the old cuts between clips move with them (or go, if
      // the set now lines up by sound). Best effort: the set itself is saved.
      if (savedSet?.id) {
        const wanted = filmed === 'separately' ? sequenceCuts(sequenceInput(angles)) : [];
        const stale = loadedSequenceCutsRef.current.filter((old) => !wanted.some((cut) => sameCut(cut, old)));
        if (wanted.length > 0 || stale.length > 0) {
          try {
            const existing = isEditMode ? await fetchMulticamAnnotations(savedSet.id, 500, 0) : [];
            const cuts = existing.filter((annotation) => annotation.type === 'SWITCH');
            const removeIds = cuts
              .filter((annotation) => stale.some((old) => sameCut({ timelineTimeMs: annotation.timelineTimeMs, targetAngleKey: annotation.targetAngleKey || '' }, old)))
              .map((annotation) => annotation.id);
            const missing = wanted.filter((cut) => !cuts.some((annotation) => Math.abs(annotation.timelineTimeMs - cut.timelineTimeMs) <= 1));
            if (missing.length > 0 || removeIds.length > 0) {
              await applyMulticamCuts(
                savedSet.id,
                missing.map((cut) => ({
                  ...toMutationInput({ type: 'SWITCH', time: cut.timelineTimeMs / 1000, endTime: null, text: '', targetAngleKey: cut.targetAngleKey }),
                  type: 'SWITCH' as const,
                  targetAngleKey: cut.targetAngleKey,
                })),
                removeIds
              );
            }
          } catch (cutError) {
            console.warn('The cuts between the clips could not be updated:', cutError);
          }
        }
      }

      setSaved(true);
      if (isEditMode) {
        setSuccess('Multicam set updated');
        redirectTimerRef.current = setTimeout(() => navigate(LIBRARY_MULTICAM_TAB), 1500);
      } else {
        // Sets start as DRAFT; the user marks them ready from the annotate screen.
        setSuccess('Multicam set created. Opening it for annotation…');
        redirectTimerRef.current = setTimeout(() => navigate(`/annotate-multicam/${savedSet.id}`), 1500);
      }
    } catch (err: any) {
      // Nothing was written, so the user can simply fix the problem and retry
      // - except after a conflict, where retrying would fail the same way
      // until the latest version is loaded. The server's validation messages
      // are written for people; network and server failures are not.
      setConflict(isMulticamConflictError(err));
      setError(friendlyErrorMessage(
        err,
        isEditMode
          ? "We couldn't save your changes. Check your connection and try again."
          : "We couldn't create the multicam set. Check your connection and try again."
      ));
    } finally {
      setSaving(false);
    }
  };

  // Get selected video IDs to exclude from other selections
  const getSelectedVideoIds = (): string[] => {
    return angles.filter(a => a.videoId).map(a => a.videoId);
  };

  // The master grid leaves out videos already chosen for B or C, as the B/C
  // dropdowns leave out the master's.
  const otherAngleVideoIds = angles.filter(a => a.angleKey !== 'A' && a.videoId).map(a => a.videoId);
  const masterCandidates = videos.filter(v => !otherAngleVideoIds.includes(v.id));
  const anglesWithVideo = angles.filter(a => a.videoId);
  const busy = saving || saved;
  // Results still arriving would change the offsets under the review step.
  const soundSyncRunning = soundSync.status === 'running' || separateAudioBusy;

  const renderNameStep = () => (
    <TextField
      fullWidth
      label="Set name"
      value={name}
      onChange={(e) => setName(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && isStepComplete(0)) {
          e.preventDefault();
          goToStep(1);
        }
      }}
      placeholder="e.g. Podcast episode 12"
      helperText="At least 3 characters."
      inputProps={{ maxLength: 120 }}
    />
  );

  const renderMasterStep = () => {
    if (loading && videos.length === 0) {
      return <LoadingState variant="cards" count={3} label="Loading your videos" />;
    }
    if (videos.length === 0) {
      return (
        <EmptyState
          compact
          icon={<VideoLibraryOutlinedIcon />}
          title="No videos in your library yet"
          description="Upload each camera angle as a video first, then come back to build the set."
          secondaryActionLabel="Go to library"
          onSecondaryAction={() => navigate('/library')}
        />
      );
    }
    return (
      <>
        <Box
          role="radiogroup"
          aria-labelledby={STEP_HEADING_ID}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5
          }}
        >
          {masterCandidates.map((video) => (
            <VideoChoice
              key={video.id}
              name="master-video"
              value={video.id}
              checked={angles[0].videoId === video.id}
              onSelect={() => handleVideoSelect('A', video.id)}
              title={video.name}
              meta={videoMetaText(video)}
              posterSrc={video.url}
            />
          ))}
        </Box>
        {otherAngleVideoIds.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Videos already used for another angle aren't listed.
          </Typography>
        )}
        {angles[0].video && (
          <TextField
            size="small"
            label="Label for this angle"
            value={angles[0].label}
            onChange={(e) => updateAngleLabel('A', e.target.value)}
            helperText="Shown on the angle switcher, for example “Wide”."
            sx={{ mt: 3, width: '100%', maxWidth: 340 }}
          />
        )}
        {angles[0].video && (
          <SpeakerNameField
            angleKey="A"
            label={angles[0].label}
            value={angles[0].speakerName ?? ''}
            onChange={(value) => updateSpeakerName('A', value)}
            sx={{ mt: 2, width: '100%', maxWidth: 340, display: 'flex' }}
          />
        )}
      </>
    );
  };

  const renderAnglesStep = () => {
    const extraAngles = angles.filter(a => a.angleKey !== 'A');
    const selectedVideoIds = getSelectedVideoIds();
    return (
      <>
        {extraAngles.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No extra angles yet.
          </Typography>
        )}
        {extraAngles.map((angle, index) => {
          const options = videos.filter(v => !selectedVideoIds.includes(v.id) || v.id === angle.videoId);
          return (
            <Box
              key={angle.angleKey}
              sx={{ pt: index === 0 ? 0 : 2.5, pb: 2.5, borderBottom: `1px solid ${brand.border}` }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Angle {angle.angleKey}
                </Typography>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
                  onClick={() => removeAngle(angle.angleKey)}
                  aria-label={`Remove angle ${angle.angleKey}`}
                >
                  Remove
                </Button>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  alignItems: 'start',
                  gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)' }
                }}
              >
                <TextField
                  select
                  size="small"
                  label="Video"
                  value={angle.videoId}
                  onChange={(e) => handleVideoSelect(angle.angleKey, e.target.value)}
                  disabled={options.length === 0}
                  helperText={
                    options.length === 0
                      ? 'Every video in your library is already in this set.'
                      : !angle.videoId
                        ? 'Choose a video, or remove this angle.'
                        : undefined
                  }
                >
                  {options.map((video) => (
                    <MenuItem key={video.id} value={video.id}>
                      {joinMeta([video.name, formatVideoDuration(video.duration)])}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  size="small"
                  label="Label"
                  value={angle.label}
                  onChange={(e) => updateAngleLabel(angle.angleKey, e.target.value)}
                  placeholder="e.g. Close-up"
                />
                <SpeakerNameField
                  angleKey={angle.angleKey}
                  label={angle.label}
                  value={angle.speakerName ?? ''}
                  onChange={(value) => updateSpeakerName(angle.angleKey, value)}
                />
              </Box>
            </Box>
          );
        })}
        {angles.length < MAX_ANGLES && (
          <Button variant="outlined" startIcon={<AddOutlinedIcon />} onClick={addAngle} sx={{ mt: 2.5 }}>
            Add angle {nextAngleKey}
          </Button>
        )}
      </>
    );
  };

  const renderSoundSyncPanel = () => {
    const running = soundSync.status === 'running';
    const outcomes = anglesWithVideo
      .filter(a => a.angleKey !== 'A')
      .map(a => lineUpResults[a.angleKey])
      .filter((outcome): outcome is LineUpOutcome => Boolean(outcome));
    const matched = outcomes.filter(isLinedUp).length;
    const total = anglesWithVideo.length - 1;
    const listening = soundSync.currentKey ? angles.find(a => a.angleKey === soundSync.currentKey) : undefined;
    return (
      <Box
        sx={{ mb: 3, p: 2, borderRadius: 2, border: `1px solid ${brand.border}`, bgcolor: 'background.default' }}
        data-testid="sound-sync-panel"
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
          <GraphicEqOutlinedIcon sx={{ color: 'primary.main', mt: 0.25 }} aria-hidden="true" />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600 }}>
              Line up by sound
            </Typography>
            <Typography variant="body2" color="text.secondary">
              PithPlay listens to each camera and lines it up with the master. Only the sound is read, a few
              minutes of each file, so it usually takes seconds. It works with MP4, MOV, WebM and MKV files that recorded
              sound; line up any other angle by picture below.
            </Typography>
          </Box>
          {running ? (
            <Button onClick={cancelSoundSync} color="inherit">Cancel</Button>
          ) : (
            <Button
              variant={soundSync.status === 'done' ? 'outlined' : 'contained'}
              startIcon={<GraphicEqOutlinedIcon />}
              onClick={() => void runSoundSync()}
              disabled={busy}
            >
              {soundSync.status === 'done' ? 'Sync again' : 'Sync by sound'}
            </Button>
          )}
        </Box>
        {running && (
          <Box sx={{ mt: 2 }} role="status" aria-live="polite">
            <LinearProgress variant="determinate" value={Math.round(soundSync.fraction * 100)} aria-label="Sync by sound progress" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
              {listening ? `Listening to ${angleDisplayName(listening)}…` : 'Listening…'} {Math.round(soundSync.fraction * 100)}%
            </Typography>
          </Box>
        )}
        {soundSync.status === 'done' && total > 0 && (
          <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 500 }} role="status">
            {matched === total
              ? total === 1 ? 'The angle is lined up. Check it below.' : 'All angles are lined up. Check them below.'
              : `${matched} of ${total} angles lined up. Check the others below.`}
          </Typography>
        )}
        {soundSync.status === 'done' && total > 0 && matched === 0 && (
          <Alert severity="info" sx={{ mt: 1.5 }} data-testid="sound-sync-no-match">
            {total === 1
              ? 'This angle doesn’t share sound with the master, so nothing was lined up.'
              : 'None of these angles share sound with the master, so nothing was lined up.'}{' '}
            They still play together, and you can cut between them. To match the moments, line each one up by
            picture below.
          </Alert>
        )}
        {soundSync.status === 'cancelled' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }} role="status">
            Stopped. Angles already lined up keep their new offsets.
          </Typography>
        )}
      </Box>
    );
  };

  const renderSyncStep = () => (
    <>
      {anglesWithVideo.length > 1 && (
        <Box sx={{ mb: 3 }}>
          <ChoiceCardGroup
            label="How should these clips play?"
            name="multicam-filmed"
            value={filmed}
            options={FILMED_OPTIONS}
            onChange={chooseFilmed}
          />
        </Box>
      )}
      {anglesWithVideo.length > 1 && filmed === 'together' && renderSoundSyncPanel()}

      {separateAudio ? (
        <TextField
          select
          size="small"
          label="Audio from"
          value="AUDIO"
          disabled
          helperText="The separate audio is the set’s sound. Remove it below to use a camera’s sound."
          sx={{ width: '100%', maxWidth: 360, mb: 3 }}
        >
          <MenuItem value="AUDIO">{recordingDisplayName(separateAudio)}</MenuItem>
        </TextField>
      ) : anglesWithVideo.length > 1 && filmed === 'separately' ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Each clip plays with its own sound, one after another.
        </Typography>
      ) : anglesWithVideo.length > 1 && (
        <TextField
          select
          size="small"
          label="Audio from"
          value={audioAngleKey}
          onChange={(e) => setAudioAngleKey(e.target.value)}
          helperText="The angle whose sound plays in the final edit."
          sx={{ width: '100%', maxWidth: 360, mb: 3 }}
        >
          {anglesWithVideo.map((angle) => (
            <MenuItem key={angle.angleKey} value={angle.angleKey}>
              {angleDisplayName(angle)}
            </MenuItem>
          ))}
        </TextField>
      )}

      {anglesWithVideo.map((angle, index) => {
        const video = angle.video;
        const durationMs = video ? parseDurationToMs(video.duration) : 60000;
        const problem = angleTimingProblem(angle);
        const isLast = index === anglesWithVideo.length - 1;

        return (
          <Box
            key={angle.angleKey}
            sx={{
              pt: index === 0 ? 0 : 3,
              pb: isLast ? 0 : 3,
              borderBottom: isLast ? 'none' : `1px solid ${brand.border}`
            }}
          >
            <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {angleDisplayName(angle)}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap title={video?.name}>
              {video ? joinMeta([video.name, formatVideoDuration(video.duration)]) : '—'}
            </Typography>

            {angle.angleKey !== 'A' && lineUpResults[angle.angleKey] && (() => {
              const { severity, text } = describeLineUp(lineUpResults[angle.angleKey]);
              return (
                <Alert severity={severity} sx={{ mt: 1.5 }} data-testid={`sound-sync-result-${angle.angleKey}`}>
                  {text}
                </Alert>
              );
            })()}

            {angle.angleKey === 'A' ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontVariantNumeric: 'tabular-nums' }}>
                {filmed === 'separately' ? 'Plays first, from 0:00.00.' : 'The master starts at 0:00.00 and sets the timeline.'}
              </Typography>
            ) : filmed === 'separately' ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontVariantNumeric: 'tabular-nums' }}>
                {`Plays after ${angleDisplayName(anglesWithVideo[index - 1])} ends, from ${formatSeconds(angle.timelineStartMs)}.`}
              </Typography>
            ) : (() => {
              // The slider spans the master's length (it used to stop at
              // 10s); the seconds field takes any value to 0.01s.
              const masterMs = masterDurationMs || 0;
              const sliderMax = Math.max(10_000, Math.ceil(masterMs / 1000) * 1000, angle.timelineStartMs);
              const usedMs = (angle.sourceOutMs ?? durationMs) - angle.sourceInMs;
              const endsOnTimelineMs = angle.timelineStartMs + Math.max(0, usedMs);
              return (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', columnGap: 1.5, rowGap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Starts on the master's timeline at
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CompareOutlinedIcon />}
                      onClick={() => setPictureAngleKey(angle.angleKey)}
                      disabled={busy || soundSyncRunning || !video || !masterVideo}
                    >
                      Line up by picture
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Slider
                      aria-label={`Timeline offset for angle ${angle.angleKey}`}
                      value={Math.min(angle.timelineStartMs, sliderMax)}
                      min={0}
                      max={sliderMax}
                      step={10}
                      onChange={(_, value) => updateAngleOffset(angle.angleKey, value as number)}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => formatSeconds(v)}
                      sx={{ flex: 1, minWidth: 0 }}
                    />
                    <TextField
                      size="small"
                      label="Seconds"
                      type="number"
                      value={angle.timelineStartMs / 1000}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        if (!isNaN(val) && val >= 0) {
                          updateAngleOffset(angle.angleKey, Math.round(val * 1000));
                        }
                      }}
                      inputProps={{ min: 0, step: 0.01, 'aria-label': `Offset in seconds for angle ${angle.angleKey}` }}
                      sx={{ width: 128, flexShrink: 0 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    How long after the master started recording this camera started. If it started
                    before the master, leave this at 0 and set Trim start below to how many seconds
                    earlier it started.
                  </Typography>
                  {video && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                      On the timeline from {formatSeconds(angle.timelineStartMs)} to {formatSeconds(endsOnTimelineMs)}
                      {masterMs > 0 && angle.timelineStartMs >= masterMs ? ' (starts after the master ends)' : ''}.
                    </Typography>
                  )}
                </Box>
              );
            })()}

            <Typography variant="body2" sx={{ fontWeight: 500, mt: 2.5, mb: 1.25 }}>
              Trim (optional)
            </Typography>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, maxWidth: 440 }}>
              <TextField
                size="small"
                label="Trim start (s)"
                type="number"
                value={angle.sourceInMs / 1000}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  if (!isNaN(val) && val >= 0) {
                    updateAngleSourceIn(angle.angleKey, Math.round(val * 1000));
                  }
                }}
                inputProps={{ min: 0, max: durationMs / 1000, step: 0.01 }}
                helperText="Seconds into this video"
              />
              <TextField
                size="small"
                label="Trim end (s)"
                type="number"
                value={angle.sourceOutMs != null ? angle.sourceOutMs / 1000 : ''}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') {
                    updateAngleSourceOut(angle.angleKey, null);
                  } else {
                    const val = parseFloat(raw);
                    if (!isNaN(val) && val > 0) {
                      updateAngleSourceOut(angle.angleKey, Math.round(val * 1000));
                    }
                  }
                }}
                inputProps={{ min: 0, max: durationMs / 1000, step: 0.01 }}
                helperText="Leave blank to use the rest"
              />
            </Box>
            {problem && (
              <Alert severity="error" sx={{ mt: 2 }}>{problem}</Alert>
            )}
          </Box>
        );
      })}

      {(() => {
        const pictureAngle = pictureAngleKey ? anglesWithVideo.find(a => a.angleKey === pictureAngleKey) : undefined;
        if (!pictureAngle?.video || !masterVideo) return null;
        return (
          <LineUpByPictureDialog
            master={{
              name: angleDisplayName(angles[0]),
              url: masterVideo.url,
              sourceInMs: angles[0].sourceInMs,
              durationMs: masterDurationMs,
            }}
            angle={{
              name: angleDisplayName(pictureAngle),
              url: pictureAngle.video.url,
              timelineStartMs: pictureAngle.timelineStartMs,
              sourceInMs: pictureAngle.sourceInMs,
              sourceOutMs: pictureAngle.sourceOutMs,
              durationMs: parseDurationToMs(pictureAngle.video.duration),
            }}
            onCancel={() => setPictureAngleKey(null)}
            onLineUp={(plan) => applyPictureLineUp(pictureAngle.angleKey, plan)}
          />
        );
      })()}

      <SeparateAudioSection
        master={masterVideo ? { url: masterVideo.url, sourceInMs: angles[0].sourceInMs, durationMs: masterDurationMs } : null}
        value={separateAudio}
        onChange={setSeparateAudio}
        onBusyChange={setSeparateAudioBusy}
        disabled={saving || saved}
      />
    </>
  );

  const renderReviewStep = () => {
    const audioAngle = angles.find(a => a.angleKey === audioAngleKey);
    const lengthMs = setLengthMs(anglesWithVideo);
    return (
      <>
        <KeyValueList
          items={[
            { label: 'Name', value: name.trim() },
            ...(anglesWithVideo.length > 1
              ? [{ label: 'Plays', value: filmed === 'separately' ? 'Joined, one after another' : 'Together, as multicam' }]
              : []),
            {
              label: 'Audio from',
              value: separateAudio
                ? recordingDisplayName(separateAudio)
                : filmed === 'separately' && anglesWithVideo.length > 1
                  ? 'Each clip’s own sound'
                  : audioAngle ? angleDisplayName(audioAngle) : '',
            },
            { label: 'Angles', value: String(anglesWithVideo.length) },
            { label: 'Length', value: lengthMs ? formatRunningTime(lengthMs) : '' }
          ]}
        />
        <Box
          component="ul"
          aria-label="Angles in this set"
          sx={{ listStyle: 'none', p: 0, m: 0, mt: 3, borderTop: `1px solid ${brand.border}` }}
        >
          {anglesWithVideo.map((angle) => (
            <Box
              component="li"
              key={angle.angleKey}
              sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: `1px solid ${brand.border}` }}
            >
              <Box sx={{ width: { xs: 88, sm: 120 }, flexShrink: 0, borderRadius: 1.5, overflow: 'hidden' }}>
                <LibraryPoster src={angle.video?.url} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {angleDisplayName(angle)}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap title={angle.video?.name}>
                  {angle.video ? joinMeta([angle.video.name, formatVideoDuration(angle.video.duration)]) : '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {timingSummary(angle)}
                  {isLinedUp(lineUpResults[angle.angleKey]) && !lineUpResults[angle.angleKey].adjusted
                    ? lineUpResults[angle.angleKey].status === 'picture' ? ' · Lined up by picture' : ' · Lined up by sound'
                    : ''}
                </Typography>
              </Box>
            </Box>
          ))}
          {separateAudio && (
            <Box
              component="li"
              data-testid="review-separate-audio"
              sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, borderBottom: `1px solid ${brand.border}` }}
            >
              <Box
                aria-hidden="true"
                sx={{ width: { xs: 88, sm: 120 }, aspectRatio: '16 / 9', flexShrink: 0, borderRadius: 1.5, bgcolor: 'background.default', border: `1px solid ${brand.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}
              >
                <GraphicEqOutlinedIcon />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {recordingDisplayName(separateAudio)}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap title={separateAudio.videoName}>
                  {joinMeta([separateAudio.videoName, separateAudio.durationMs > 0 ? formatRunningTime(separateAudio.durationMs) : null])}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {`Starts at ${formatSeconds(separateAudio.timelineStartMs)}`}
                  {separateAudio.sourceInMs > 0 ? ` · Trim ${formatSeconds(separateAudio.sourceInMs)} to end` : ''}
                  {' · The set’s sound'}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      </>
    );
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderNameStep();
      case 1:
        return renderMasterStep();
      case 2:
        return renderAnglesStep();
      case 3:
        return renderSyncStep();
      case 4:
        return renderReviewStep();
      default:
        return null;
    }
  };

  const saveLabel = saving
    ? (isEditMode ? 'Saving…' : 'Creating…')
    : saved
      ? (isEditMode ? 'Saved' : 'Created')
      : (isEditMode ? 'Save changes' : 'Create multicam set');

  const step = STEPS[activeStep];
  const stepDescription = activeStep === LAST_STEP && isEditMode
    ? 'Check your changes before you save them.'
    : step.description;

  return (
    <PageContainer width="narrow">
      <PageHeader
        title={isEditMode ? 'Edit multicam set' : 'New multicam set'}
        subtitle="Add each camera angle, line them up, and choose which one carries the audio."
        backTo={{ to: LIBRARY_MULTICAM_TAB, label: 'Library' }}
      />

      {loadProblem ? (
        <Alert
          severity="error"
          action={
            loadProblem.canRetry ? (
              <Button color="inherit" size="small" onClick={() => loadData()} disabled={loading}>
                Retry
              </Button>
            ) : (
              <Button color="inherit" size="small" component={RouterLink} to={LIBRARY_MULTICAM_TAB}>
                Back to library
              </Button>
            )
          }
        >
          {loadProblem.message}
        </Alert>
      ) : isEditMode && !initialLoadDone ? (
        <LoadingState variant="list" count={3} label="Loading multicam set" />
      ) : (
        <>
          <MulticamHowItWorksNote />
          <Stepper nonLinear alternativeLabel activeStep={activeStep} sx={stepperSx}>
            {STEPS.map((item, index) => (
              <Step key={item.label} completed={index < activeStep} disabled={busy || soundSyncRunning || !canReachStep(index)}>
                <StepButton onClick={() => goToStep(index)}>{item.label}</StepButton>
              </Step>
            ))}
          </Stepper>

          <Box
            ref={stepRegionRef}
            tabIndex={-1}
            role="group"
            aria-labelledby={STEP_HEADING_ID}
            sx={{ outline: 'none' }}
          >
            <Section id={STEP_SECTION_ID} title={step.title} description={stepDescription}>
              {renderStepContent(activeStep)}

              {error && (
                <Alert
                  severity="error"
                  sx={{ mt: 3 }}
                  action={conflict ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={handleReloadAfterConflict}
                      disabled={reloading}
                    >
                      {reloading ? 'Reloading…' : 'Reload'}
                    </Button>
                  ) : undefined}
                >
                  {error}
                </Alert>
              )}

              <Divider sx={{ mx: -2.5, mt: 3, mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                {activeStep > 0 ? (
                  <Button onClick={() => goToStep(activeStep - 1)} disabled={busy}>
                    Back
                  </Button>
                ) : (
                  <span />
                )}
                {activeStep < LAST_STEP ? (
                  <Button
                    variant="contained"
                    onClick={() => goToStep(activeStep + 1)}
                    disabled={!isStepComplete(activeStep) || soundSyncRunning}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={busy || !isStepComplete(activeStep)}
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <CheckOutlinedIcon />}
                  >
                    {saveLabel}
                  </Button>
                )}
              </Box>
            </Section>
          </Box>
        </>
      )}

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </PageContainer>
  );
};

export default MulticamNew;
